import {
  AppleAirPlaySecurityProfilePresetsService,
  AppleRestrictionsProfilePresetsService,
  AppleWifiProfilePresetsService,
} from '@rocksoft/mdm-sdk-v2';

import { AppleProfilePresetTypeConfig } from './apple-profile-preset.types';

/**
 * Factory function that creates profile preset type configurations.
 * Use this with Angular's DI to provide configs to the store.
 *
 * @example
 * ```typescript
 * {
 *   provide: APPLE_PROFILE_PRESET_CONFIGS,
 *   useFactory: createAppleProfilePresetConfigs,
 *   deps: [
 *     AppleAirPlaySecurityProfilePresetsService,
 *     AppleRestrictionsProfilePresetsService,
 *     AppleWifiProfilePresetsService,
 *   ],
 * }
 * ```
 */
export function createAppleProfilePresetConfigs(
  airplayService: AppleAirPlaySecurityProfilePresetsService,
  restrictionsService: AppleRestrictionsProfilePresetsService,
  wifiService: AppleWifiProfilePresetsService
): AppleProfilePresetTypeConfig[] {
  return [
    {
      profileType: 'airplay-security',
      apiService: {
        getAll: () => airplayService.getAirPlaySecurityPresets({ apiVersion: '2.0' }),
        add: (preset) =>
          airplayService.addAirPlaySecurityPreset({ apiVersion: '2.0', appleAirPlaySecurityPresetAdd: preset }),
        update: (id, patch) => airplayService.updateAirPlaySecurityPreset({ id, apiVersion: '2.0', operation: patch }),
        delete: (id) => airplayService.deleteAirPlaySecurityProfilePreset({ id, apiVersion: '2.0' }),
      },
    },
    {
      profileType: 'restrictions-profile',
      apiService: {
        getAll: () => restrictionsService.getRestrictionsProfilePresets({ apiVersion: '2.0' }),
        add: (preset) =>
          restrictionsService.addRestrictionsProfilePreset({
            apiVersion: '2.0',
            appleRestrictionsProfilePresetAdd: preset,
          }),
        update: (id, patch) =>
          restrictionsService.updateRestrictionsProfilePreset({ id, apiVersion: '2.0', operation: patch }),
        delete: (id) => restrictionsService.deleteRestrictionsProfilePreset({ id, apiVersion: '2.0' }),
      },
    },
    {
      profileType: 'wifi-profile',
      apiService: {
        getAll: () => wifiService.getWifiProfilePresets({ apiVersion: '2.0' }),
        add: (preset) => wifiService.addWifiProfilePreset({ apiVersion: '2.0', appleWifiProfilePresetAdd: preset }),
        update: (id, patch) => wifiService.updateWifiProfilePreset({ id, apiVersion: '2.0', operation: patch }),
        delete: (id) => wifiService.deleteWifiProfilePreset({ id, apiVersion: '2.0' }),
      },
    },
  ];
}
